if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.usedPartialAddFallback = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              const errorMessage = response.description || response.message;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(errorMessage, 'INVALID');

              // Shopify can return a 422 after adding the available inventory
              // when the requested quantity is too high. Reconcile the drawer
              // and badge so that partial add is visible instead of looking lost.
              const cartChanged = await this.refreshCartAfterInventoryError(variantId, errorMessage);
              if (cartChanged) {
                this.resolveCartLinesUpdate(linesUpdateDeferred);
              } else {
                linesUpdateDeferred?.reject(new Error(errorMessage));
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty') && !this.usedPartialAddFallback) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.usedPartialAddFallback = false;

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async refreshCartAfterInventoryError(variantId, errorMessage) {
        if (!this.cart) return false;

        const partialAddMatch = errorMessage?.match(/only\s+(\d+)\s+items?\s+(?:was|were)\s+added\b/i);
        if (!partialAddMatch) return false;

        const addedQuantity = Number.parseInt(partialAddMatch[1], 10);
        if (!Number.isFinite(addedQuantity) || addedQuantity < 1) return false;

        try {
          const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
          const sections = {};

          // A cart mutation can become visible just after Shopify has rendered
          // the 422 response. Retry incomplete section responses briefly instead
          // of leaving the drawer in its previous empty state.
          for (let attempt = 0; attempt < 3; attempt += 1) {
            const sectionsResponse = await fetch(
              `${routes.cart_url}?sections=${sectionIds.join(',')}&_=${Date.now()}`,
              {
                cache: 'no-store',
              }
            );
            if (!sectionsResponse.ok) throw new Error(`Cart sections returned ${sectionsResponse.status}`);

            const responseSections = await sectionsResponse.json();
            sectionIds.forEach((sectionId) => {
              if (typeof responseSections[sectionId] === 'string') {
                sections[sectionId] = responseSections[sectionId];
              }
            });

            if (typeof sections['cart-drawer'] === 'string') break;
            await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)));
          }

          if (typeof sections['cart-drawer'] !== 'string') throw new Error('Cart drawer section was incomplete');

          this.cart.renderContents({ id: variantId, sections });
          return true;
        } catch (error) {
          console.warn('Cart changed, but its drawer could not be refreshed.', error);
          this.usedPartialAddFallback = true;
          this.showPartialAddFallback(addedQuantity, errorMessage);
          return true;
        }
      }

      showPartialAddFallback(addedQuantity, errorMessage) {
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (cartIcon) {
          const currentCount = Number.parseInt(
            cartIcon.querySelector('.cart-count-bubble [aria-hidden="true"]')?.textContent,
            10,
          ) || 0;
          const updatedCount = currentCount + addedQuantity;
          let countBubble = cartIcon.querySelector('.cart-count-bubble');

          if (!countBubble) {
            countBubble = document.createElement('div');
            countBubble.className = 'cart-count-bubble';
            countBubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
            cartIcon.append(countBubble);
          }

          countBubble.querySelector('[aria-hidden="true"]').textContent = updatedCount;
          countBubble.querySelector('.visually-hidden').textContent = `${updatedCount} items`;
        }

        let recoveryMessage = this.cart.querySelector('.cart-drawer__recovery-message');
        if (!recoveryMessage) {
          recoveryMessage = document.createElement('p');
          recoveryMessage.className = 'cart-drawer__recovery-message';
          recoveryMessage.setAttribute('role', 'status');

          const drawerHeader = this.cart.querySelector('.drawer__header');
          if (drawerHeader) drawerHeader.insertAdjacentElement('afterend', recoveryMessage);
        }
        recoveryMessage.textContent = `${errorMessage} View your cart to confirm the available quantity.`;

        if (this.cart.classList.contains('is-empty')) {
          const emptyTitle = this.cart.querySelector('.cart__empty-text');
          if (emptyTitle) emptyTitle.textContent = errorMessage;

          const emptyAction = this.cart.querySelector('.cart-drawer__warnings a.button');
          if (emptyAction) {
            emptyAction.href = routes.cart_url;
            emptyAction.textContent = 'View cart';
          }
        }

        this.cart.open();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
